<script setup lang="ts">
import { computed, type Component } from 'vue';
import type { NxHue } from '../lib/hue';
import { opacityStyle, resolveAccent, type NxAccentLevel, type NxAccentTone } from './accent';

const props = defineProps<{
  /** Optional title shown in a compact header band */
  title?: string;
  /**
   * An icon (a component, e.g. from `@blpde/nx-icons`) before the title. Decorative: it is
   * hidden from assistive tech, so the title has to carry the meaning. Needs a `title`.
   */
  icon?: Component;
  /**
   * A muted line after the title, set off by a „·“ — a file name, an id, a status. Normal case
   * and weight, one line ending in „…“; it gives way first, so the actions are never pushed
   * out. Needs a `title`.
   */
  subtitle?: string;
  /**
   * When `true`, the header keeps the height it has with an `xs` action button even when the
   * actions are absent — for a panel whose actions come and go (a selection, a permission), so
   * the row does not jump. Off: the header is as tall as its content.
   */
  stableHead?: boolean;
  /** When `true`, removes the default surface/border (use inside a parent that already styles the surface) */
  bare?: boolean;
  /** When `true`, adds a neutral perimeter border; off by default. `bare` still wins. */
  bordered?: boolean;
  /** When `true`, removes inner padding */
  flush?: boolean;
  /**
   * When `true`, the panel takes its parent's full height and the body grows
   * into the room under the header: a flex column that may shrink below its
   * content and scrolls when it must — a dashboard tile, cards of one row at
   * equal height. A child with `flex: 1` takes the rest of the body.
   */
  fill?: boolean;
  /**
   * Caps the panel's height at a CSS length (a token like
   * `var(--nxt-area-size-2xl)` or `50vh`): the header keeps its height and
   * the body scrolls in place once the content is taller. Unset: the panel is
   * as tall as its content. With `fill` as well, the panel fills its parent
   * but never grows above the cap.
   */
  maxHeight?: string;
  /**
   * When `true`, the body leaves the type to its content: no font size, line
   * height or paragraph reset of its own — for a panel that hosts a whole app
   * view (a calendar, a board) whose type comes from the view itself.
   */
  inheritType?: boolean;
  /**
   * Coloured strip on the left edge in an intent tone — a risk, priority or
   * status card („kritisch“ = `danger`). Unset: no strip.
   */
  tone?: NxAccentTone;
  /** The strip in a category hue instead (groups, not meaning). Wins over `tone`. */
  hue?: NxHue;
  /**
   * The accent from any colour token, named without `--nxt-` (`'warning-bg'`,
   * `'hue-teal'`, `'chart-3'`) — for colours a document picks, e.g. a
   * presentation layer. Wins over `hue` and `tone`; not a token name → ignored.
   */
  accent?: string | null;
  /**
   * How much of the accent shows (`NxAccentLevel`): `edge` (default) the strip,
   * `fill` the strip and a light wash over the panel, `tint` no strip but a
   * tinted header band, `muted` no accent and the panel steps back.
   */
  accentLevel?: NxAccentLevel;
  /** 0–1: the strip's width (0 = none) and a token accent's strength. Default 1. */
  accentEmphasis?: number;
  /** 0–1: fades the whole panel, e.g. things a presentation layer pushes back. */
  opacity?: number;
  /**
   * `default` — a panel on the surface tokens. `group` — a field group inside
   * a section (a record mask's subsection): it follows the design's groups axis
   * (`data-nx-groups`, the `--nxd-group-*` tokens) — a card one shadow step
   * above the surface (flat on a hairline surface), or under `rule` no card
   * and no clipping, its title an accent line over a rule; the title follows
   * the caps and ink axes. Its body is a single-column grid.
   */
  variant?: 'default' | 'group';
  /**
   * Attributes bound onto the header element itself — the `#header` slot sits
   * inside it, so this is the way to make the whole header strip a drag handle
   * (`draggable`, `onDragstart`, …) or give it a class or test hook. A
   * `draggable` header shows the grab cursor.
   */
  headerAttrs?: Record<string, unknown>;
}>();

const accent = computed(() => resolveAccent(props, '--panel-accent'));
const accentStyle = computed(() => ({
  ...accent.value.vars,
  ...opacityStyle(props.opacity),
  ...(props.maxHeight ? { '--panel-max': props.maxHeight } : {}),
}));
const accentClasses = computed(() => {
  const { hasColor, level } = accent.value;
  return {
    'has-accent': hasColor && (level === 'edge' || level === 'fill'),
    'is-accent-fill': hasColor && level === 'fill',
    'is-accent-tint': hasColor && level === 'tint',
    'is-muted': level === 'muted',
  };
});
</script>

<template>
  <section
    class="cn-panel"
    :class="[
      {
        bare,
        bordered,
        flush,
        fill,
        'has-max-height': !!maxHeight,
        'inherit-type': inheritType,
        'variant-group': variant === 'group',
      },
      accentClasses,
    ]"
    :style="accentStyle"
  >
    <header
      v-if="title || $slots.header || $slots.actions"
      v-bind="headerAttrs"
      class="cn-panel-head"
      :class="{ 'is-stable-head': stableHead }"
    >
      <slot name="header">
        <template v-if="title">
          <component
            :is="icon"
            v-if="icon"
            class="cn-panel-icon"
            :class="variant === 'group' ? 'nxt-icon-md' : 'nxt-icon-lg'"
            aria-hidden="true"
          />
          <h3 class="cn-panel-title" :class="{ 'has-subtitle': !!subtitle }">{{ title }}</h3>
          <span v-if="subtitle" class="cn-panel-subtitle">{{ subtitle }}</span>
        </template>
      </slot>
      <div v-if="$slots.actions" class="cn-panel-actions">
        <slot name="actions" />
      </div>
    </header>
    <div class="cn-panel-body">
      <slot />
    </div>
  </section>
</template>

<style scoped>
.cn-panel {
  background: var(--nxt-panel-bg);
  color: var(--nxt-fg);
  /* Surface-treatment tokens — remapped per data-nx-surface (I-160). */
  border: var(--nxd-surface-border);
  border-radius: var(--nxd-surface-radius);
  box-shadow: var(--nxd-surface-shadow);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-width: 0;
}
/* A field group (variant="group") follows the groups axis (data-nx-groups,
   I-2510): the --nxd-group-* tokens are unset on `card`, so every fallback is
   the card a field group always was — on the surface tokens, one shadow step
   above the surface for contrast against the section it sits on (I-2882).
   `rule` takes the card away. */
.cn-panel.variant-group {
  border: var(--nxd-group-border, none);
  box-shadow: var(--nxd-group-shadow, var(--nxt-panel--group-shadow, var(--nxt-shadow-md)));
  border-radius: var(--nxd-group-radius, var(--nxd-surface-radius));
  background: var(--nxd-group-bg, var(--nxd-surface-bg));
  /* A card clips to its rounded corners; a rule group has no corners and no
     inset, so clipping would cut the focus ring of a field on its edge. */
  overflow: var(--nxd-group-overflow, hidden);
  position: relative;
}
/* The group's card shadow is flat on a hairline surface: values on custom
   properties of the nearest data-nx-surface (`card` resets them), never a rule
   keyed on an ancestor — a card page under a hairline design keeps its shadow
   (I-2766). */
:global([data-nx-surface='card']) {
  --nxt-panel--group-shadow: initial;
}
:global([data-nx-surface='hairline']) {
  --nxt-panel--group-shadow: none;
}
.cn-panel.bordered {
  border: var(--nxt-hairline) solid var(--nxt-border);
}
.cn-panel.bare {
  background: transparent;
  border: none;
  border-radius: 0;
}
/* Accent strip (accent/hue/tone), as wide as its emphasis. After `.bare`, so a
   bare panel keeps its strip too. */
.cn-panel.has-accent {
  border-inline-start: calc(var(--nxt-hairline) * 3 * var(--panel-accent-emphasis)) solid
    var(--panel-accent);
}
.cn-panel.is-accent-fill {
  background: color-mix(
    in oklab,
    var(--panel-accent) calc(3% + 4% * var(--panel-accent-emphasis)),
    var(--nxt-panel-bg)
  );
}
.cn-panel.is-muted {
  color: var(--nxt-fg-muted);
  filter: saturate(0.7);
}

/* The items (icon, title, subtitle) pack to the start; the actions reach the row's end by
   their own auto margin, so a header without actions stays together. */
.cn-panel-head {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: var(--nxt-space-xs);
  min-height: var(--nxt-control-size-md);
  padding: var(--nxt-space-xs) var(--nxt-space-md);
  border-bottom: var(--nxt-hairline) solid var(--nxt-panel-border);
  background: var(--nxt-panel-header-bg);
}
.cn-panel.variant-group > .cn-panel-head {
  gap: var(--nxt-space-sm);
  min-height: auto;
  padding-block: var(--nxt-space-xs);
  padding-inline: var(--nxd-group-padding, var(--nxt-space-sm));
  background: var(--nxd-group-bg, var(--nxd-surface-head-fill));
  border-bottom: var(--nxd-group-title-rule, var(--nxt-hairline) solid var(--nxt-border));
}
/* Stable height (stableHead): the content box keeps the height of an `xs` NxButton — its
   line, the padding the button takes (0.5em of its text size, twice) and its border — so the
   row is as tall with the actions as without them. The padding stays the header's own. */
.cn-panel-head.is-stable-head {
  box-sizing: content-box;
  min-height: calc(
    var(--nxt-font-size-xs) * (var(--nxt-line-height-control) + var(--nxt-space-scale)) + 2 *
      var(--nxt-hairline)
  );
}
/* The whole header is a drag handle when the host binds `draggable` on it. */
.cn-panel-head[draggable='true'] {
  cursor: grab;
}
.cn-panel.is-accent-tint > .cn-panel-head {
  background: var(--panel-accent-subtle-bg);
}
.cn-panel.bare > .cn-panel-head {
  border-bottom: none;
  padding: var(--nxt-space-2xs) var(--nxt-space-xs);
}

.cn-panel-title {
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-semibold);
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--nxt-fg-muted);
  margin: 0;
}

/* The group's title is a small heading: it follows the caps axis
   (data-nx-caps) — as written by default, in caps at the label size — and as a
   rule (groups: rule) is an accent line in semibold, as a card title bold in
   the ink colour (data-nx-ink). One line, ending in „…“. */
.cn-panel.variant-group > .cn-panel-head > .cn-panel-title {
  flex-grow: 1;
  min-width: 0;
  overflow: hidden;
  font-weight: var(--nxd-group-title-weight, var(--nxt-font-weight-bold));
  font-size: var(--nxd-caption-size, var(--nxt-font-size-sm));
  letter-spacing: var(--nxd-caption-tracking, normal);
  text-transform: var(--nxd-caption-transform, none);
  color: var(--nxd-group-title-fg, var(--nxd-ink, var(--nxt-fg)));
  white-space: nowrap;
  text-overflow: ellipsis;
}

.cn-panel-icon {
  flex: none;
  color: var(--nxt-fg);
}
/* The title holds its width next to a subtitle: the subtitle is what gives way. */
.cn-panel-title.has-subtitle {
  flex-shrink: 0;
  white-space: nowrap;
}
.cn-panel-subtitle {
  min-width: 0;
  overflow: hidden;
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-medium);
  color: var(--nxt-fg-muted);
  white-space: nowrap;
  text-overflow: ellipsis;
}
.cn-panel-subtitle::before {
  content: '·';
  margin-inline-end: var(--nxt-space-xs);
}

.cn-panel-actions {
  /* At the row's end, whatever the title side holds (icon, title, subtitle). */
  margin-inline-start: auto;
  flex: none;
  display: flex;
  align-items: center;
  gap: var(--nxt-space-2xs);
  color: var(--nxt-fg-muted);
}

.cn-panel-body {
  padding: var(--nxt-space-sm) var(--nxt-space-md);
  min-width: 0;
  font-size: var(--nxt-font-size-sm);
  line-height: var(--nxt-line-height-normal);
}
/* A field group stacks its content in one column. Before `flush` and `fill`,
   which take its padding away or make the body a scrolling column. */
.cn-panel.variant-group > .cn-panel-body {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: var(--nxt-space-sm);
  padding-block: var(--nxt-space-sm);
  padding-inline: var(--nxd-group-padding, var(--nxt-space-sm));
}
/* The global type reset sizes bare <p>/<ul> at 16px medium; the body sets it. */
.cn-panel:not(.inherit-type) > .cn-panel-body :deep(:is(p, ul, ol)) {
  font-size: inherit;
  font-weight: inherit;
}
/* Inherit type: the hosted view keeps the type it has outside a panel. */
.cn-panel.inherit-type > .cn-panel-body {
  font-size: inherit;
  line-height: inherit;
}
/* Child combinators: a panel nested in a flush/bare/tinted panel shares the
   scope id, so a descendant rule would strip the inner panel too. */
.cn-panel.flush > .cn-panel-body {
  padding: 0;
}
/* Fill: the panel is as tall as its parent, the header keeps its height and
   the body takes the rest — shrinking below its content, scrolling past it. */
.cn-panel.fill {
  height: 100%;
  min-height: 0;
}
.cn-panel.fill > .cn-panel-head {
  flex-shrink: 0;
}
.cn-panel.fill > .cn-panel-body {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
  overflow: auto;
}
/* Max height: the same body rules as fill, capped instead of filling the
   parent — the header keeps its height, the body shrinks and scrolls. */
.cn-panel.has-max-height {
  max-height: var(--panel-max);
}
.cn-panel.has-max-height > .cn-panel-head {
  flex-shrink: 0;
}
.cn-panel.has-max-height > .cn-panel-body {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
  overflow: auto;
}
</style>
